import { useEffect, useRef } from 'react'
import { useAbly } from 'ably/react'
import type * as Ably from 'ably'
import { useAuthContext } from '@/context'

export function orgAblyChannelName(orgId?: string | null): string {
    return orgId ? `org:${orgId}` : 'org:__none__'
}

/** Subscribe to org-scoped Ably events without nesting ChannelProvider (avoids duplicate provider crashes). */
export function useOrgAblyChannel(
    eventOrCallback: string | ((message: Ably.Message) => void),
    callback?: (message: Ably.Message) => void
) {
    const ably = useAbly()
    const { org } = useAuthContext()
    const channelName = orgAblyChannelName(org?.id)
    const callbackRef = useRef(callback)
    callbackRef.current = typeof eventOrCallback === 'function' ? eventOrCallback : callback

    useEffect(() => {
        if (!ably) return
        const channel = ably.channels.get(channelName)
        const event = typeof eventOrCallback === 'string' ? eventOrCallback : undefined
        const listener = (message: Ably.Message) => {
            callbackRef.current?.(message)
        }

        if (event) {
            channel.subscribe(event, listener)
            return () => channel.unsubscribe(event, listener)
        }
        channel.subscribe(listener)
        return () => channel.unsubscribe(listener)
    }, [ably, channelName, eventOrCallback])
}

/** Portrait flows use `global` when org is not yet available. */
export function usePortraitAblyChannel(callback: (message: Ably.Message) => void) {
    const ably = useAbly()
    const { org } = useAuthContext()
    const channelName = org?.id ? `org:${org.id}` : 'global'
    const callbackRef = useRef(callback)
    callbackRef.current = callback

    useEffect(() => {
        if (!ably) return
        const channel = ably.channels.get(channelName)
        const listener = (message: Ably.Message) => callbackRef.current(message)
        channel.subscribe(listener)
        return () => channel.unsubscribe(listener)
    }, [ably, channelName])
}
